import { isBasicElement, isElement, isHtmlElement, isNode } from "./guards";
import { stringEquals } from "./strings";

/**
 * @template T
 * @typedef {(item: T) => boolean} Predicate
 */
/** @typedef {Predicate<Node>} NodePredicate */
/** @typedef {Predicate<Element>} ElementPredicate */
/** @typedef {Predicate<import("./defs").BasicElement>} BasicElementPredicate */

/**
 * Checks if the DOM is available to access and use.
 * 
 * @copyright MIT License from https://github.com/chrisdholt/exenv-es6.
 * @returns {boolean}
 */
export function canUseDOM() {
  return !!(typeof window !== "undefined" && window.document && window.document.createElement);
}

/**
 * @param {Node} n
 * @param {string?} slot 
 */
export function isNodeInSlot(n, slot = null) {
  if (!isBasicElement(n)) return false;
  const s = n.getAttribute("slot");
  return stringEquals(s, slot) ?? !slot;
}

/**
 * @param {import("./defs").BasicElement} n
 * @param {string?} slot 
 */
export function isBasicElementInSlot(n, slot = null) {
  const s = n.getAttribute("slot");
  return stringEquals(s, slot) ?? !slot;
}

/**
 * Returns all displayed elements inside of a root node that match a provided selector.
 * 
 * @param {HTMLElement?} rootNode 
 * @param {string?} selector 
 */
export function getDisplayedNodes(rootNode, selector) {
  if (!rootNode || !selector || !isHtmlElement(rootNode)) {
    return;
  }
  
  const nodes = [...rootNode.querySelectorAll(selector)];
  
  // offsetParent will be null if the element isn't currently displayed,
  // so this will allow us to operate only on visible nodes.
  return nodes.filter(node => /** @type {HTMLElement} */ (node).offsetParent !== null);
}

/**
 * Returns the nonce used in the page, if any.
 *
 * Based on https://github.com/cssinjs/jss/blob/master/packages/jss/src/DomRenderer.js
 * @returns {string?}
 */
function getNonce() {
  const node = document.querySelector('meta[property="csp-nonce"]');
  if (node) {
    return node.getAttribute("content");
  } else {
    return null;
  }
}

/**
 * @returns {boolean}
 */
export function canUseForcedColors() {
  return (
    canUseDOM() &&
    (window.matchMedia("(forced-colors: none)").matches ||
    window.matchMedia("(forced-colors: active)").matches)
  );
}
  
/**
 * @readonly
 * @enum {string}
 */
export const StyleFormats = {
  css: "css",
  postCss: "postcss",
}

/**
 * @public
 * @readonly
 * @enum {string}
 */
export const BlockResize = {
  none: "none",
  both: "both",
  horizontal: "horizontal",
  vertical: "vertical"
}

/**
 * @param {HTMLElement|SVGElement} element
 * @param {string} name 
 * @returns {boolean}
 */
export function isAttributeTruthy(element, name) {
  let v = element.getAttribute(name);
  return v != null && v !== "false";
}

/**
 * @param {Node} node
 * @returns {IterableIterator<Node>}
 */
export function *forEachChildReverse(node) {
	const children = node.childNodes;
	for (let i = children.length - 1; i > -1; i--) {
		yield children[i];
	}
}

/**
 * Inserts a new {@link HTMLStyleElement} with the given CSS to the page.
 * @param {string} css The CSS to be inserted into the new <style> element.
 * @param {string|undefined} layer If non-empty, wraps the css in a CSS layer.
 * @param {Element|undefined} parent The parent under which the style element will be attached. Defaults to the <head> tag.
 * @param {((element: HTMLStyleElement) => void)|undefined} handler A function that, if provided, will be run against the style element before it is added to the document.
 */
export function addStyleSheet(css, layer = undefined, parent = undefined, handler = undefined) {
	if (!!layer) {
		css = `@layer ${layer} {
${css}
}`;
	}
	parent = parent ?? document.head;

	const style = document.createElement('style');
	style.setAttribute('type', 'text/css');
	style.innerHTML = window.BU.policy.createHTML(css);

  if (typeof(handler) === "function") {
    handler(style);
  }

	const beforeChild = parent.querySelector(":scope > :nth-last-child(1 of style, link)");
	document.head.insertBefore(style, beforeChild);
}

/**
 * Inserts a new {@link HTMLLinkElement} with the given CSS to the page.
 * @param {string} rel The attribute describing the the linked resource's relationship to the document.
 * @param {string|URL|undefined} href The URL with which to reference the resource.
 * @param {Element|undefined} parent The parent under which the link element will be attached. Defaults to the <head> tag.
 * @param {((element: HTMLLinkElement) => void)|undefined} handler A function that, if provided, will be run against the link element before it is added to the document.
 */
export function addLinkElement(rel, href, parent = undefined, handler = undefined) {
	parent = parent ?? document.head;

  const actualHref = typeof(href === "string")
    ? href
    : href instanceof URL
      ? href
      : undefined;
  if (!actualHref) {
    return;
  }

  const existing = parent.querySelector(`link[href='${actualHref}']`);
  if (existing) {
    return;
  }

	const link = document.createElement('link');
  link.rel = rel;
  if (typeof(href) === "string") {
    link.href = href;
  } else if (href instanceof URL) {
    link.href = href.href;
  }

  if (typeof(handler) === "function") {
    handler(link);
  }

	const beforeChild = parent.querySelector(":scope > :nth-last-child(1 of style, link)");
	document.head.insertBefore(link, beforeChild);
}

/**
 * Iterates over ancestor nodes starting at `node`, proceeding along the sequence via each `next` selector, and terminating the sequence when the `predicate` fails.
 * @template {Node} T
 * @param {Node?} node The node from which to begin gathering ancestors.
 * @param {string} next The selector string that leads us to the next ancestor in the sequence. If empty, the immediate `.parentElement` will be used. Defaults to "".
 * @param {((node: Node) => node is T)?} guard Evaluates the current node and then each parent to be included. If the guard fails, the sequence terminates immediately. If null, returns every `next` ancestor. Defaults to null.
 * @returns {IterableIterator<T>}
 */
export function *ancestors(node, next = "", guard = null) {
	let c = node;
	while (c !== null) {
		if (!guard || guard(c)) {
			yield /** @type {T} */ (c);
			c = next ? c.parentElement?.closest(next) ?? null : c.parentElement;
		} else {
			break;
		}
	}
}

/**
 * @param {Node} source
 * @param {Node} node 
 * @param {number} index 
 * @returns {void}
 */
export function moveNode(source, node, index = -1) {
	if (index < 0) {
		source.appendChild(node);
	}
	let p = source.parentNode;
	index = Math.max(index, p?.childNodes.length ?? 0 - 1);
	let ithNode = source.childNodes.item(index)
	source.parentNode?.insertBefore(node, ithNode);
}

/**
 * @template {Node} T
 * @param {Node} node
 * @param {(n: Node) => n is T} typeGuard
 * @returns {(nodeName: string) => IterableIterator<T>}
 */
export function iterateChildrenByType(node, typeGuard) {
	return function*(nodeName) {
		for (let child of node.childNodes) {
			if (typeGuard(child) && (!nodeName || stringEquals(child.nodeName, nodeName))) {
				yield child;
			}
		}
	}
}

/**
 * 
 * @param {Element} source
 * @param {string} nodeName 
 * @returns {IterableIterator<Element>}
 */
export function iterateChildElements(source, nodeName) {
  return iterateChildrenByType(source, isElement)(nodeName);
}

/**
 * @param {HTMLElement} el
 */
export function toggleDisplay(el) {
  el.style.display = el.style.display === "none" ? "" : "none";
}

/**
 * @template {Element} T
 * @param {T} el
 * @param {boolean|undefined} deep 
 * @returns {T}
 */
export function cloneElement(el, deep) {
  return /** @type {T} */ ((/** @type {Element} */ (el)).cloneNode(deep));
}

/**
 * Generates a Promise that represents the execution of an HTML event. Allows you to await a given event with async JS code.
 * 
 * @param {Element} el
 * @param {string} event 
 * @returns {Promise<void>}
 */
export function getEventPromise(el, event) {
  return new Promise((resolve) => {
    const listener = () => {
      el.removeEventListener(event, listener);
      resolve();
    }
    el.addEventListener(event, listener);
  })
}

/**
 * Calculates the depth of a given Node in the DOM tree.
 * @param {Node} node The node whose depth is to be calculated.
 * @param {Node?} [ancestor=null] The node from which depth will be relative. If null, it will default to `document.getRootNode()`.
 * @returns {number} The depth of the given node. `document.getRootNode()` will return a depth of 1 if `ancestor` is `null`.
 */
export function getNodeDepth(node, ancestor = null) {
  let depth = 1;
  let root = ancestor ?? document.getRootNode();
  while (node.parentNode && node.parentNode !== root) {
      depth++;
      node = node.parentNode;
  }
  return depth;
}

/**
 * Returns a depth-sorted array of DOM nodes.
 * @template {Node} T
 * @param {T[]} nodes - The list of nodes to be sorted.
 * @param {Node} [ancestor=null] The node from which depth will be relative. If null, it will default to `document.getRootNode()`.
 * @returns {{node: T, depth: number}[]} A depth-sorted array of objects containing nodes and their depths.
 * @example
 * // html
 * <template>
 *   <nav>
 *     <ul>
 *       <li>Item 1
 *         <ul>
 *           <li>Item 1.1</li>
 *           <li>Item 1.2</li>
 *         </ul>
 *       </li>
 *       <li>Item 2</li>
 *     </ul>
 *   </nav>
 * </template>
 * 
 * // js
 * const t = document.querySelector("template");
 * const items = t.content.querySelectorAll(":scope nav li");
 * depthify([...items]).forEach(({ node, depth }) => {
 *   console.log(node.textContent.trim(), depth);
 * });
 * // "Item 1", 4
 * // "Item 2", 4
 * // "Item 1.1", 6
 * // "Item 1.2", 6
 */
export function depthify(nodes, ancestor) {
  const nodesWithDepths = nodes.map((node, index) => ({
    node,
    depth: getNodeDepth(node, ancestor),
    index,
  }));

  // Sort nodes by depth first and by their original document order if they have the same depth
  nodesWithDepths.sort((a, b) => {
    if (a.depth === b.depth) {
        return a.index - b.index; // Maintain original document order for same-depth nodes
    }
    return a.depth - b.depth; // Sort by depth
  });

  return nodesWithDepths;
}

/**
 * @param {object} obj 
 * @returns {string?}
 */
export function extractTitle(obj) {
  if (typeof(obj) === "undefined" || obj === null) return null;
  if ("title" in obj) {
    return obj.title?.toString() ?? null;
  }
  if ("label" in obj) {
    return obj.label?.toString() ?? null;
  }
  if ("name" in obj) {
    return obj.name?.toString() ?? null;
  }
  return "";
}

/**
 * @param {string} html
 * @returns {HTMLElement}
 */
export function createHtml(html) {
  const template = document.createElement("template");
  template.innerHTML = window.BU.policy.createHTML(html.trim());
  if (template.content.childNodes.length !== 1) {
    throw new Error("The `html` to create must have exactly 1 root element.");
  }
  return /** @type {HTMLElement} */ (template.content.firstChild);
}

/**
 * Asynchronously waits until the element matching `selector` exists on the page, then returns it.
 * Returns immediately if the element already exists.
 * 
 * @template {Element} TElement
 * @param {string} selector
 * @returns {Promise<TElement|null>}
 */
export function waitForElement(selector) {
  return new Promise(resolve => {
    const element = document.querySelector(selector);
    if (element) {
      return resolve(/** @type {TElement} */ (element));
    }

    const observer = new MutationObserver(_ => {
        const elem = document.querySelector(selector);
        if (elem) {
            resolve(/** @type {TElement} */ (elem));
            observer.disconnect();
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
  });
}

// Note: never add an `isInViewport` method. You can simply use the new Baseline `.checkVisibility()` method instead.
